import { IconDownload } from '@iwoca/icons';
import { Button, Card } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { FileUpload } from 'components/Registration/FileUpload/FileUpload';
import { useFormContext } from 'react-hook-form';
import { TDocTypeDE, TOnboardingFormValuesDE } from '../onboardingFormDE.types';
import styles from './OnboardingDocumentsDE.module.css';

// Signed partner questionnaire template — same asset used on the DE no-products
// page (src/components/ProductItems/NoProducts.tsx).
const PARTNER_QUESTIONNAIRE_PDF_URL =
  'https://static.iwoca.com/pdf/de/Partner-Fragebogen-v2.pdf';

type TDocumentField =
  | 'brokerQuestionnaireFile'
  | 'hrTradeCertificateFile'
  | 'license34cFile'
  | 'certifiedIdFile';

type TProps = {
  disabled?: boolean;
};

export const OnboardingDocumentsDE = ({ disabled = false }: TProps) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<TOnboardingFormValuesDE>();

  const uploadedLabel = t`Document uploaded`;

  const uploadSteps: {
    step: number;
    docType: TDocTypeDE;
    field: TDocumentField;
    label: string;
    instruction: string;
  }[] = [
    {
      step: 2,
      docType: 'broker_questionnaire',
      field: 'brokerQuestionnaireFile',
      label: t`Signed partner questionnaire`,
      instruction: t`Upload the signed questionnaire.`,
    },
    {
      step: 3,
      docType: 'hr_trade_certificate',
      field: 'hrTradeCertificateFile',
      label: t`Commercial register extract / trade licence`,
      instruction: t`Upload your commercial register extract (Handelsregisterauszug) or trade licence (Gewerbeschein).`,
    },
    {
      step: 4,
      docType: 'license_34c',
      field: 'license34cFile',
      label: t`§34c trade permit`,
      instruction: t`Upload your official trade permit under §34c GewO.`,
    },
    {
      step: 5,
      docType: 'certified_id',
      field: 'certifiedIdFile',
      label: t`ID document`,
      instruction: t`A single file containing both sides of your official photo ID (passport or ID card).`,
    },
  ];

  return (
    <>
      <h2>
        <Trans>Documents</Trans>
      </h2>
      <Card
        className={clsx(styles.documentsContainer, disabled && styles.disabled)}
      >
        {/* Step 1 — download the questionnaire template (no upload) */}
        <div className={styles.step}>
          <span className={styles.badge}>1</span>
          <div className={styles.stepContent}>
            <p className={styles.stepLabel}>
              <Trans>Partner questionnaire</Trans>
            </p>
            <p className={styles.instruction}>
              <Trans>
                Download the questionnaire, sign it, and then upload the signed
                copy.
              </Trans>
            </p>
            <div className={styles.downloadRow}>
              <span className={styles.downloadRowLabel}>
                <Trans>Download partner questionnaire</Trans>
              </span>
              <Button
                variant="secondary-alt"
                size="small"
                href={PARTNER_QUESTIONNAIRE_PDF_URL}
                target="_blank"
                rel="noreferrer"
                disabled={disabled}
              >
                <IconDownload width={16} height={16} />
                <Trans>Download PDF</Trans>
              </Button>
            </div>
          </div>
        </div>

        {/* Steps 2–5 — document uploads */}
        {uploadSteps.map(({ step, docType, field, label, instruction }) => (
          <div className={styles.step} key={field}>
            <span className={styles.badge}>{step}</span>
            <div className={styles.stepContent}>
              <FileUpload<TDocTypeDE>
                docType={docType}
                label={label}
                instruction={instruction}
                uploadedLabel={uploadedLabel}
                selectedFile={watch(field)}
                errorMessage={errors[field]?.message}
                disabled={disabled}
                onChange={(file) =>
                  setValue(field, file, { shouldValidate: true })
                }
              />
            </div>
          </div>
        ))}
      </Card>
    </>
  );
};
